import React, {
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { subEquipmentStepCanProceed } from "../../../lib/inspection_validator";
import CheckText from "./CheckText";
import CheckList from "./CheckList";
import CheckCheckBox from "./CheckCheckBox";
import CheckOptions from "./CheckOptions";
import CheckFile from "./CheckFile";
import CheckTitle from "./CheckTitle";
import GpmInput from "../Widget/GpmInput";
import CorrectedPressureCalc from "../Widget/CorrectedPressureCalc";
import { getCheck } from "../../../lib/calculation";
import wizard_css from "../../inspection.module.css";
import PercentCalc from "../Widget/PercentCalc";
import CathodicProtectionCalc from "../Widget/CathodicProtectionCalc";
import { MainContext } from "../../../main-context";

export default function Check({
  check,
  equipment,
  topLevel,
  tosubequipmentinspection,
  station,
  data,
  set_data,
  saveTextCommentAndPhoto,
  deleteComment,
  index,
  type,
  addInterceptorValue,
  setCheck,
  params,
}) {
  const SUCCESSFUL_CHECK_VALUE = "S";
  /* TODO: include constants from a common place. Currently shared in a few classes.  */
  const INSPECTION_STATUS_OUTOFSERVICE = 6;
  const INSPECTION_STATUS_NOT_USED = 5;

  const main_context = useContext(MainContext);

  const [interceptors, set_interceptors] = useState({});

  const addInterceptorMethod = useCallback((name, method) => {
    set_interceptors((prev) => ({ ...prev, [name]: method }));
  }, []);

  const addInterceptor = useRef(addInterceptorValue ?? addInterceptorMethod);

  useEffect(() => {
    let changed = false;
    const check_copy = { ...check };
    if (subEquipmentStepCanProceed(check_copy) !== true) {
      check_copy.options.forEach((item) => {
        if (
          "" + item.valueName === SUCCESSFUL_CHECK_VALUE &&
          item.disabled !== true
        ) {
          changed = true;
          item.disabled = true;
        }
      });
    }
    if (check_copy.interconnected) {
      check_copy.interconnected.forEach((item) => {
        /* hide both buttons if inspection status is out of service */
        const key =
          item.equipment_id + "-" + data.operationId + "-" + params.type;
        const offline_done = main_context.done_intercompany[key] === true;
        if (
          +item.status !== INSPECTION_STATUS_OUTOFSERVICE &&
          !!item.out_of_service !== true
        ) {
          if (
            !!item.step_complete === true ||
            +item.status === INSPECTION_STATUS_NOT_USED
          ) {
            if (
              item.show_perform_inspection !== false ||
              item.show_view_offline !== false ||
              item.show_view_inspection !== true
            ) {
              changed = true;
              item.show_perform_inspection = false;
              item.show_view_offline = false;
              item.show_view_inspection = true;
            }
          } else {
            if (offline_done) {
              if (
                item.show_perform_inspection !== false ||
                item.show_view_offline !== true ||
                item.show_view_inspection !== false
              ) {
                changed = true;
                item.show_perform_inspection = false;
                item.show_view_offline = true;
                item.show_view_inspection = false;
              }
            } else {
              if (
                item.show_perform_inspection !== true ||
                item.show_view_offline !== false ||
                item.show_view_inspection !== false
              ) {
                changed = true;
                item.show_perform_inspection = true;
                item.show_view_offline = false;
                item.show_view_inspection = false;
              }
            }
          }
        }
      });
    }
    if (changed) {
      const data_copy = { ...data };
      data_copy.checks[index] = check_copy;
      set_data(data_copy);
    }
  }, [
    check,
    data,
    index,
    main_context.done_intercompany,
    params.type,
    set_data,
  ]);

  const setCheckMethod = useCallback(
    (value, check_id, topLevel, type = "value") => {
      const data_copy = { ...data };
      const checkMatch = getCheck(data_copy, check_id, topLevel);
      if (checkMatch) {
        checkMatch[type] = value;
      }

      for (const method of Object.values(interceptors)) {
        method(value, data_copy);
      }
      set_data(data_copy);
    },
    [data, interceptors, set_data]
  );

  const setCheckValue = useMemo(
    () => setCheck ?? setCheckMethod,
    [setCheck, setCheckMethod]
  );

  const setPhoto = useCallback(
    (elem, check_id, topLevel) => {
      if (elem.files && elem.files[0]) {
        var fr = new FileReader();
        fr.onload = function (e) {
          setCheckValue(e.target.result, check_id, topLevel, "photo");
        };
        fr.readAsDataURL(elem.files[0]);
      }
    },
    [setCheckValue]
  );

  return (
    <MainContext.Consumer>
      {() => (
        <>
          <div className={`${check.metadata?.hidden ? "hidden" : ""}`}>
            <div>
              {!!check.metadata?.h2 && (
                <CheckTitle
                  title={check.metadata.h2}
                  check={check}
                  equipment={equipment}
                  station={station}
                ></CheckTitle>
              )}

              {check.metadata?.widget === "gpmInput" && (
                <GpmInput
                  check={check}
                  topLevel={topLevel}
                  index={index}
                  setCheckValue={setCheckValue}
                ></GpmInput>
              )}
              {check.metadata?.widget === "correctedPressureCalc" && (
                <CorrectedPressureCalc
                  check={check}
                  equipment={equipment}
                  topLevel={topLevel}
                  tosubequipmentinspection={tosubequipmentinspection}
                  station={station}
                  data={data}
                  set_data={set_data}
                  saveTextCommentAndPhoto={saveTextCommentAndPhoto}
                  deleteComment={deleteComment}
                  index={index}
                  setCheckValue={setCheckValue}
                  addInterceptor={addInterceptor.current}
                  type={type}
                  params={params}
                ></CorrectedPressureCalc>
              )}
              {check.metadata?.widget === "cathodicProtectionCalc" && (
                <CathodicProtectionCalc
                  check={check}
                  equipment={equipment}
                  topLevel={topLevel}
                  tosubequipmentinspection={tosubequipmentinspection}
                  station={station}
                  data={data}
                  set_data={set_data}
                  saveTextCommentAndPhoto={saveTextCommentAndPhoto}
                  deleteComment={deleteComment}
                  index={index}
                  setCheckValue={setCheckValue}
                  addInterceptor={addInterceptor.current}
                  type={type}
                  params={params}
                ></CathodicProtectionCalc>
              )}
              {check.metadata?.widget === "percentCalc" && (
                <PercentCalc
                  check={check}
                  equipment={equipment}
                  topLevel={topLevel}
                  tosubequipmentinspection={tosubequipmentinspection}
                  station={station}
                  data={data}
                  set_data={set_data}
                  saveTextCommentAndPhoto={saveTextCommentAndPhoto}
                  deleteComment={deleteComment}
                  index={index}
                  setCheckValue={setCheckValue}
                  addInterceptor={addInterceptor.current}
                  type={type}
                  params={params}
                ></PercentCalc>
              )}
              {check.metadata?.widget !== "gpmInput" &&
                check.metadata?.widget !== "correctedPressureCalc" &&
                check.metadata?.widget !== "cathodicProtectionCalc" &&
                check.metadata?.widget !== "percentCalc" && (
                  <>
                    {check.type === 0 && (
                      <CheckText
                        check={check}
                        topLevel={topLevel}
                        inspection={data}
                        equipment={equipment}
                        index={index}
                        setCheckValue={setCheckValue}
                      ></CheckText>
                    )}
                    {check.type === 1 && (
                      <CheckCheckBox
                        check={check}
                        equipment={equipment}
                        topLevel={topLevel}
                        tosubequipmentinspection={tosubequipmentinspection}
                        station={station}
                        data={data}
                        set_data={set_data}
                        saveTextCommentAndPhoto={saveTextCommentAndPhoto}
                        deleteComment={deleteComment}
                        index={index}
                        setCheckValue={setCheckValue}
                        addInterceptor={addInterceptor.current}
                        type={type}
                        params={params}
                      ></CheckCheckBox>
                    )}
                    {check.type === 2 && (
                      <CheckOptions
                        check={check}
                        equipment={equipment}
                        topLevel={topLevel}
                        tosubequipmentinspection={tosubequipmentinspection}
                        station={station}
                        data={data}
                        set_data={set_data}
                        saveTextCommentAndPhoto={saveTextCommentAndPhoto}
                        deleteComment={deleteComment}
                        index={index}
                        setCheckValue={setCheckValue}
                        addInterceptor={addInterceptor.current}
                        type={type}
                        params={params}
                      ></CheckOptions>
                    )}
                    {check.type === 3 && (
                      <CheckList
                        check={check}
                        topLevel={topLevel}
                        saveTextCommentAndPhoto={saveTextCommentAndPhoto}
                        deleteComment={deleteComment}
                        index={index}
                        setCheckValue={setCheckValue}
                        type={type}
                      ></CheckList>
                    )}
                    {check.type === 4 && (
                      <CheckFile
                        check={check}
                        setCheckValue={setCheckValue}
                        topLevel={topLevel}
                      ></CheckFile>
                    )}
                  </>
                )}
            </div>
            {/* <!-- do not put a file input box for LIST_TYPE = 3 which has one file per comment --> */}
            {check.type !== 3 &&
              !(check.children && check.children.length) &&
              !!check.opensItem && (
                <div className="btn btn-default">
                  <i
                    className="fa fa-2x fa-camera"
                    style={{
                      float: "left",
                      marginRight: "10px",
                      color: "gray",
                    }}
                  ></i>
                  <input
                    type="file"
                    onChange={(event) =>
                      setPhoto(event.target, check.id, topLevel)
                    }
                    capture="camera"
                    accept="image/*"
                  />
                </div>
              )}
          </div>

          {/* <!-- the interconnected items may appear multiple times for each check, depending on how much
equipment is not completed --> */}

          {!!check.interconnected && check.interconnected.length > 0 && (
            <fieldset
              className="interconnected"
              style={{ marginTop: "30px", marginBottom: "50px" }}
            >
              <legend style={{ marginBottom: 0 }}>Related Inspections</legend>
              {check.interconnected.map((inter, index) => (
                <div
                  className={`${wizard_css["options"]} ${wizard_css["interconnected-items"]}`}
                  key={index}
                >
                  <h4>{inter.name}</h4>
                  Status:{" "}
                  <strong>
                    {inter.out_of_service ? "Out of Service" : "In Service"}
                  </strong>
                  <br />
                  {!!inter.show_perform_inspection && (
                    <span
                      onClick={() =>
                        tosubequipmentinspection(
                          inter.equipment_id,
                          inter.check_id
                        )
                      }
                      className="btn btn-xs btn-block btn-danger"
                    >
                      Perform Inspection
                    </span>
                  )}
                  {!!inter.show_view_inspection && (
                    <span
                      onClick={() =>
                        tosubequipmentinspection(
                          inter.equipment_id,
                          inter.check_id
                        )
                      }
                      className="btn btn-xs btn-block btn-success"
                    >
                      View Inspection
                    </span>
                  )}
                  {!!inter.show_view_offline && (
                    <span
                      onClick={() =>
                        tosubequipmentinspection(
                          inter.equipment_id,
                          inter.check_id
                        )
                      }
                      className="btn btn-xs btn-block btn-warning"
                    >
                      View Inspection
                    </span>
                  )}
                </div>
              ))}
            </fieldset>
          )}
        </>
      )}
    </MainContext.Consumer>
  );
}
